import { ComponentType } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { CircleIcon } from '@/components/icons/CircleIcon';
import { IconProps } from '@/components/icons/CircleIcon';
import { SquareIcon } from '@/components/icons/SquareIcon';
import { TriangleIcon } from '@/components/icons/TriangleIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { GraphVizIconShape } from '@/modules/graphViz/graphVizNodeConfig';
import { COLORS } from '@/styles/tokens/colors';

/** SquareIcon rotated 45° to represent a diamond/rhombus shape */
function DiamondShapeIcon(props: IconProps) {
  return (
    <SquareIcon {...props} sx={{ transform: 'rotate(45deg)', ...props.sx }} />
  );
}

interface ShapeOption {
  label: string;
  value: GraphVizIconShape;
  Icon: ComponentType<IconProps>;
}

const SHAPE_OPTIONS: ShapeOption[] = [
  { label: 'Circle', value: GraphVizIconShape.Circle, Icon: CircleIcon },
  { label: 'Square', value: GraphVizIconShape.Square, Icon: SquareIcon },
  { label: 'Triangle', value: GraphVizIconShape.Triangle, Icon: TriangleIcon },
  {
    label: 'Diamond',
    value: GraphVizIconShape.Diamond,
    Icon: DiamondShapeIcon,
  },
];

const ICON_SIZE = 16;

interface IconShapePickerProps {
  currentShape: GraphVizIconShape;
  /** When true, multiple different shapes are selected — no checkmark shown */
  isMixed?: boolean;
  onShapeChange: (shape: GraphVizIconShape) => void;
  disabled?: boolean;
}

export function IconShapePicker({
  currentShape,
  isMixed = false,
  onShapeChange,
  disabled = false,
}: IconShapePickerProps) {
  const CurrentShapeIcon =
    SHAPE_OPTIONS.find((opt) => opt.value === currentShape)?.Icon ?? CircleIcon;
  const showCheck = !isMixed;

  return (
    <ButtonWithPopover
      variant="transparent"
      size="xs"
      disabled={disabled}
      label={<CurrentShapeIcon size={ICON_SIZE} />}
      highlightWhenOpen
      popperProps={{
        // Anchor the menu to the bottom-left of the trigger so it opens
        // downward and extends to the right, matching the design.
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        PaperProps: { sx: { minWidth: 160 } },
      }}
    >
      <SelectItemGroupLabel label="Icon shape" />
      {SHAPE_OPTIONS.map(({ label, value, Icon }) => {
        const isActive = showCheck && value === currentShape;
        return (
          <MenuItem
            key={value}
            icon={<Icon size={ICON_SIZE} />}
            label={label}
            iconAfter={
              isActive ? (
                <CheckIcon size={ICON_SIZE} color={COLORS.TEAL[600]} />
              ) : undefined
            }
            onClick={() => onShapeChange(value)}
          />
        );
      })}
    </ButtonWithPopover>
  );
}
